import { create } from 'zustand';
import qs from 'qs';
import { validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection, readCreatedDocumentId } from '@/lib/strapi';
import {
  VolunteerOpportunity,
  VolunteerOpportunitiesResponse,
  VolunteerOpportunityResponse,
  VolunteerFrequency,
  VolunteerOpportunityFrequency,
} from '@/types';

export interface OpportunityMutationData {
  title: string;
  organization: string;
  groupId?: number;
  groupDocumentId?: string; // Use documentId instead of id for Strapi 5
  description: string;
  location: string;
  salary: string;
  frequency: VolunteerOpportunityFrequency;
  volunteersNeeded: number;
  startDate?: string;
  endDate?: string;
  timeStart?: string;
  timeEnd?: string;
  applyUrl?: string;
  contactName?: string;
  contactEmail?: string;
  image?: File | null;
}

export type CreateOpportunityData = OpportunityMutationData;
export type UpdateOpportunityData = OpportunityMutationData;

interface UpdateOpportunityResult {
  success: boolean;
  opportunity?: VolunteerOpportunity;
  error?: string;
}

interface CreateOpportunityResult {
  success: boolean;
  /** The new opportunity's id (for the activity log), when Strapi returned one. */
  documentId?: string;
}

interface VolunteerState {
  opportunities: VolunteerOpportunity[];
  currentOpportunity: VolunteerOpportunity | null;
  selectedFrequency: VolunteerFrequency;
  loading: boolean;
  error: string | null;
  fetchOpportunities: (frequency?: VolunteerFrequency) => Promise<void>;
  fetchOpportunity: (documentId: string, userId?: number) => Promise<void>;
  fetchInterestState: (documentId: string, userId?: number) => Promise<void>;
  setSelectedFrequency: (frequency: VolunteerFrequency) => void;
  toggleApply: (documentId: string) => Promise<void>;
  hasUserApplied: (documentId: string, userId: number) => boolean;
  toggleInterest: (documentId: string, userId: number) => Promise<boolean | null>;
  hasUserInterested: (documentId: string, userId: number) => boolean;
  createOpportunity: (data: CreateOpportunityData) => Promise<CreateOpportunityResult>;
  updateOpportunity: (documentId: string, data: UpdateOpportunityData) => Promise<UpdateOpportunityResult>;
  deleteOpportunity: (documentId: string) => Promise<boolean>;
  getSortedOpportunities: () => VolunteerOpportunity[];
}

const baseUrl = '/api/strapi';
const volunteerMutationBaseUrl = '/api/volunteers';
let latestOpportunityRequestId = 0;
let currentOpportunityMutationVersion = 0;

const uploadOpportunityImage = async (image?: File | null): Promise<number | null> => {
  if (!image) return null;

  validateFileSize(image);

  const formData = new FormData();
  formData.append('files', image);

  const uploadRes = await fetch(`${baseUrl}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!uploadRes.ok) {
    throw new Error('Failed to upload opportunity image');
  }

  const uploadedFiles = await uploadRes.json();
  return uploadedFiles[0]?.id ?? null;
};

const buildOpportunityPayload = (
  data: OpportunityMutationData,
  imageId?: number | null,
): Record<string, unknown> => {
  const opportunityData: Record<string, unknown> = {
    title: data.title,
    organization: data.organization,
    description: data.description,
    location: data.location,
    salary: data.salary || null,
    frequency: data.frequency,
    volunteersNeeded: data.volunteersNeeded,
    startDate: data.startDate || null,
    endDate: data.endDate || null,
    timeStart: data.timeStart || null,
    timeEnd: data.timeEnd || null,
    applyUrl: data.applyUrl || null,
    contactName: data.contactName || null,
    contactEmail: data.contactEmail || null,
  };

  if (typeof imageId !== 'undefined') {
    opportunityData.image = imageId;
  }

  if (data.groupDocumentId) {
    opportunityData.group = { connect: [data.groupDocumentId] };
  } else if (data.groupId) {
    opportunityData.group = data.groupId;
  }

  return opportunityData;
};

const getResponseErrorMessage = async (
  response: Response,
  fallbackMessage: string,
): Promise<string> => {
  try {
    const payload = await response.clone().json() as {
      error?: string | { message?: string };
      message?: string;
    };

    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error;
    }

    if (typeof payload.error === 'object' && typeof payload.error?.message === 'string' && payload.error.message.trim()) {
      return payload.error.message;
    }

    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
  } catch (error) {
    console.error('src/store/volunteerStore.ts', error);
    // Fall through to text parsing.
  }

  try {
    const text = await response.text();
    if (text.trim()) {
      return text;
    }
  } catch (error) {
    console.error('src/store/volunteerStore.ts', error);
    // Ignore and return fallback.
  }

  return fallbackMessage;
};

const updateOpportunityApplicants = (
  opportunity: VolunteerOpportunity,
  applicants: { id: number }[],
  interestedCount: number,
): VolunteerOpportunity => ({
  ...opportunity,
  applicants,
  applicantCount: interestedCount,
});

const normalizeApplicants = (applicants: unknown): { id: number }[] => {
  if (!Array.isArray(applicants)) return [];

  return applicants
    .filter((applicant): applicant is { id: number } => (
      typeof applicant === 'object'
      && applicant !== null
      && typeof (applicant as { id?: unknown }).id === 'number'
    ))
    .map((applicant) => ({ id: applicant.id }));
};

const reconcileViewerApplicant = (
  applicants: { id: number }[],
  viewerUserId?: number,
  interested?: boolean,
): { id: number }[] => {
  if (typeof viewerUserId !== 'number' || typeof interested !== 'boolean') {
    return applicants;
  }

  const withoutViewer = applicants.filter((applicant) => applicant.id !== viewerUserId);
  return interested ? [...withoutViewer, { id: viewerUserId }] : withoutViewer;
};

const mergeInterestState = (
  opportunity: VolunteerOpportunity,
  interestData: unknown,
  viewerUserId?: number,
): VolunteerOpportunity => {
  const data = typeof interestData === 'object' && interestData !== null
    ? interestData as { applicants?: unknown; applicantCount?: unknown; interested?: unknown }
    : {};

  const applicants = reconcileViewerApplicant(
    normalizeApplicants(data.applicants),
    viewerUserId,
    typeof data.interested === 'boolean' ? data.interested : undefined,
  );

  return updateOpportunityApplicants(opportunity, applicants, applicants.length);
};

export const useVolunteerStore = create<VolunteerState>((set, get) => ({
  opportunities: [],
  currentOpportunity: null,
  selectedFrequency: 'all',
  loading: false,
  error: null,

  fetchOpportunities: async (frequency?: VolunteerFrequency) => {
    try {
      set({ loading: true, error: null });

      const filters: Record<string, unknown> = {
        deletedAt: { $null: true }, // Exclude soft-deleted
      };
      const freq = frequency ?? get().selectedFrequency;

      if (freq && freq !== 'all') {
        filters.frequency = { $eq: freq };
      }

      const opportunities = await fetchStrapiCollection<VolunteerOpportunitiesResponse['data'][number]>(
        'volunteer-opportunities',
        {
          filters,
          populate: ['image', 'group'],
          sort: ['createdAt:desc'],
        },
      );

      set({ opportunities, loading: false });
    } catch (error) {
      console.error('src/store/volunteerStore.ts', error);
      set({ error: (error as Error).message, loading: false });
    }
  },

  setSelectedFrequency: (frequency: VolunteerFrequency) => {
    set({ selectedFrequency: frequency });
    get().fetchOpportunities(frequency);
  },

  fetchOpportunity: async (documentId: string, userId?: number) => {
    const requestId = ++latestOpportunityRequestId;
    const mutationVersionAtStart = currentOpportunityMutationVersion;

    try {
      set((state) => ({
        loading: true,
        error: null,
        currentOpportunity: state.currentOpportunity?.documentId === documentId ? state.currentOpportunity : null,
      }));

      const query = qs.stringify({
        populate: {
          image: { fields: ['url', 'alternativeText'] },
          group: { fields: ['id', 'documentId', 'name'] },
          applicants: { fields: ['id'] },
        },
      }, { encodeValuesOnly: true });
      const url = `${baseUrl}/volunteer-opportunities/${documentId}?${query}`;
      const interestUrl = `${baseUrl}/volunteer-opportunities/${documentId}/interest-state`;
      const [res, interestRes] = await Promise.all([
        fetch(url, { cache: 'no-store' }),
        fetch(interestUrl, { cache: 'no-store' }),
      ]);

      if (!res.ok) throw new Error('Failed to fetch opportunity');

      const data: VolunteerOpportunityResponse = await res.json();
      let mergedOpportunity = data.data;

      if (interestRes.ok) {
        const interestJson = await interestRes.json();
        mergedOpportunity = mergeInterestState(data.data, interestJson.data, userId);
      }

      set((state) => {
        if (requestId !== latestOpportunityRequestId) {
          return state;
        }

        if (mutationVersionAtStart !== currentOpportunityMutationVersion) {
          return { ...state, loading: false };
        }

        return {
          ...state,
          currentOpportunity: mergedOpportunity,
          opportunities: state.opportunities.map((opportunity) =>
            opportunity.documentId === documentId ? mergedOpportunity : opportunity
          ),
          loading: false,
          error: null,
        };
      });
    } catch (error) {
      console.error('src/store/volunteerStore.ts', error);
      set((state) => (
        requestId === latestOpportunityRequestId
          ? { ...state, error: (error as Error).message, loading: false }
          : state
      ));
    }
  },

  fetchInterestState: async (documentId: string, userId?: number) => {
    try {
      const url = `${baseUrl}/volunteer-opportunities/${documentId}/interest-state`;

      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch volunteer interest state');

      const { data } = await res.json();
      currentOpportunityMutationVersion += 1;

      set((state) => ({
        currentOpportunity: state.currentOpportunity?.documentId === documentId
          ? mergeInterestState(state.currentOpportunity, data, userId)
          : state.currentOpportunity,
        opportunities: state.opportunities.map((opportunity) =>
          opportunity.documentId === documentId
            ? mergeInterestState(opportunity, data, userId)
            : opportunity
        ),
      }));
    } catch (err) {
      console.error('Error fetching volunteer interest state:', err);
    }
  },

  toggleApply: async (documentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/volunteer-opportunities/${documentId}/toggle-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle apply');

      const { data } = await res.json();

      currentOpportunityMutationVersion += 1;

      set((state) => ({
        currentOpportunity: state.currentOpportunity?.documentId === documentId
          ? {
              ...state.currentOpportunity,
              applicants: data.applicants ?? (data.applied ? [...(state.currentOpportunity.applicants || [])] : []),
              applicantCount: data.applicantCount,
            }
          : state.currentOpportunity,
      }));
    } catch (err) {
      console.error('Error toggling apply:', err);
    }
  },

  hasUserApplied: (documentId: string, userId: number) => {
    const opp = get().currentOpportunity;
    if (!opp || opp.documentId !== documentId) return false;
    return opp.applicants?.some(a => a.id === userId) ?? false;
  },

  toggleInterest: async (documentId: string, userId: number) => {
    try {
      const res = await fetch(`${baseUrl}/volunteer-opportunities/${documentId}/toggle-interested`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle interest');

      const { data } = await res.json();
      currentOpportunityMutationVersion += 1;

      set((state) => ({
        currentOpportunity: state.currentOpportunity?.documentId === documentId
          ? mergeInterestState(state.currentOpportunity, data, userId)
          : state.currentOpportunity,
        opportunities: state.opportunities.map((opportunity) =>
          opportunity.documentId === documentId
            ? mergeInterestState(opportunity, data, userId)
            : opportunity
        ),
      }));

      return data.interested as boolean;
    } catch (err) {
      console.error('Error toggling interest:', err);
      return null;
    }
  },

  hasUserInterested: (documentId: string, userId: number) => {
    const opp = get().currentOpportunity;
    if (!opp || opp.documentId !== documentId) return false;
    return opp.applicants?.some((applicant) => applicant.id === userId) ?? false;
  },

  createOpportunity: async (data: CreateOpportunityData) => {
    try {
      set({ loading: true, error: null });

      const imageId = await uploadOpportunityImage(data.image);
      const opportunityData = buildOpportunityPayload(data, imageId);

      const res = await fetch(`${baseUrl}/volunteer-opportunities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: opportunityData }),
      });

      if (!res.ok) throw new Error('Failed to create opportunity');
      const documentId = await readCreatedDocumentId(res);

      // Refresh the list
      await get().fetchOpportunities();
      set({ loading: false });
      return { success: true, documentId };
    } catch (error) {
      console.error('src/store/volunteerStore.ts', error);
      set({ error: (error as Error).message, loading: false });
      return { success: false };
    }
  },

  updateOpportunity: async (documentId: string, data: UpdateOpportunityData) => {
    try {
      set({ loading: true, error: null });

      const imageId = data.image ? await uploadOpportunityImage(data.image) : undefined;
      const opportunityData = buildOpportunityPayload(data, imageId);

      const res = await fetch(`${volunteerMutationBaseUrl}/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: opportunityData }),
      });

      if (!res.ok) {
        throw new Error(await getResponseErrorMessage(res, 'Failed to update opportunity'));
      }

      const responseData: VolunteerOpportunityResponse = await res.json();
      const updatedOpportunity = responseData.data;
      currentOpportunityMutationVersion += 1;

      set((state) => ({
        loading: false,
        currentOpportunity: state.currentOpportunity?.documentId === documentId
          ? updatedOpportunity
          : state.currentOpportunity,
        opportunities: state.opportunities.map((opportunity) =>
          opportunity.documentId === documentId ? updatedOpportunity : opportunity
        ),
      }));

      return { success: true, opportunity: updatedOpportunity };
    } catch (error) {
      console.error('src/store/volunteerStore.ts', error);
      const message = (error as Error).message;
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  deleteOpportunity: async (documentId: string) => {
    try {
      const res = await fetch(`${volunteerMutationBaseUrl}/${documentId}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete opportunity');

      // Remove from local state
      currentOpportunityMutationVersion += 1;
      set((state) => ({
        opportunities: state.opportunities.filter(o => o.documentId !== documentId),
        currentOpportunity: state.currentOpportunity?.documentId === documentId ? null : state.currentOpportunity,
      }));

      return true;
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      return false;
    }
  },

  getSortedOpportunities: () => {
    const opportunities = get().opportunities;

    const sortByDate = (a: VolunteerOpportunity, b: VolunteerOpportunity) => {
      const dateA = a.startDate || a.date || a.createdAt;
      const dateB = b.startDate || b.date || b.createdAt;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    };

    return [...opportunities].sort(sortByDate);
  },
}));

